import React, { memo, useMemo } from "react";
import { CircularProgress, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Certification } from "../../Models/certification";
import colors from "../../Constants/colors";
import { ComoSelect, ComoSelectOption } from "../Common/ComoSelect";

interface CertificationSelectProps {
  selectedCertification: number | null;
  setSelectedCertification: (value: number | null) => void;
  certifications: Certification[];
  isLoading: boolean;
  isError: boolean;
  disabled?: boolean;
}

const CertificationSelect: React.FC<CertificationSelectProps> = memo(
  ({
    selectedCertification,
    setSelectedCertification,
    certifications,
    isLoading,
    isError,
    disabled,
  }) => {
    const { t } = useTranslation("createQuestionList");

    const options: ComoSelectOption[] = useMemo(() => {
      if (isLoading) {
        return [
          {
            id: "__loading__",
            label: t("createQuestionList.loadingCertifications"),
          },
        ];
      }
      if (isError) {
        return [
          {
            id: "__error__",
            label: t("createQuestionList.errorLoadingCertifications"),
          },
        ];
      }
      if (!certifications || certifications.length === 0) {
        return [
          {
            id: "__empty__",
            label: t("createQuestionList.noCertificationsAvailable"),
          },
        ];
      }
      return certifications.map((cert) => ({
        id: cert.id,
        label: cert.name,
      }));
    }, [certifications, isLoading, isError, t]);

    const selectedOption = useMemo(
      () => options.find((o) => o.id === selectedCertification),
      [options, selectedCertification]
    );

    const isOptionDisabled = (option: ComoSelectOption) =>
      disabled ||
      isLoading ||
      option.id === "__loading__" ||
      option.id === "__error__" ||
      option.id === "__empty__";

    const handleSelect = (option: ComoSelectOption) => {
      if (typeof option.id === "number") {
        setSelectedCertification(option.id);
      }
    };

    const placeholder = isLoading ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} sx={{ color: colors.gray900 }} />
        {t("createQuestionList.loadingCertifications")}
      </Box>
    ) : (
      t("createQuestionList.selectCertificationCategory")
    );

    return (
      <ComoSelect
        options={options}
        selectedOption={selectedOption}
        onSelect={handleSelect}
        placeholder={
          typeof placeholder === "string"
            ? placeholder
            : t("createQuestionList.selectCertificationCategory")
        }
        disabled={isOptionDisabled}
        maxHeight={300}
        sx={{ mb: 0 }}
      />
    );
  }
);

CertificationSelect.displayName = "CertificationSelect";
export default CertificationSelect;
